import {
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  CONTENT_ARGS,
  Metadata,
  pick,
} from '../../../utils/spds/metadata';
import { and, equal } from 'macro-decorators';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Button from '../core/button.gjs';
import FlexRow from '../layout/flex/row.gjs';
import Super from '../core/base.gjs';
import shiftFocusToParent from '../../../modifiers/shift-focus-to-parent';
import styles from './tag.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

const BASIC = 'basic';
const FILTER = 'filter';
const INTERACTIVE = 'interactive';
const TYPES = [BASIC, FILTER, INTERACTIVE];

const METADATA = Metadata.define({
  description: `
    Tag component used to categorize content. Tags can represent keywords, filters, or people
    and are grouped to describe an item or search request.

    Three variants are supported:
    - **basic**: static, non-interactive label
    - **filter**: removable tag with close button (requires @onClose)
    - **interactive**: clickable tag (requires @onClick)
  `,
  extends: [
    pick(ACTION_ARGS, ['onClick', 'onClose']),
    pick(CONTENT_ARGS, ['icon', { name: 'label', required: true }]),
    pick(COMPONENT_STATE_ARGS, ['disabled']),
  ],
  meta: {
    small: Arg.boolean({ description: 'Renders the tag at a smaller size' }),
    type: Arg.oneOf(TYPES, { description: 'Tag variant type' })
      .required()
      .forbids([
        {
          name: 'onClick',
          when: [BASIC, FILTER],
          reason: "only the 'interactive' variant supports @onClick",
        },
        {
          name: 'onClose',
          when: [BASIC, INTERACTIVE],
          reason: "only the 'filter' variant supports @onClose",
        },
      ])
      .requires([
        {
          name: 'onClick',
          when: [INTERACTIVE],
          reason: "the 'interactive' variant requires an @onClick handler",
        },
        {
          name: 'onClose',
          when: [FILTER],
          reason: "the 'filter' variant requires an @onClose handler",
        },
      ]),
  },
  name: 'Tag',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-Web-Components?m=auto&node-id=23480-130749&t=ADL8oK1s8842xDHJ-1',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/v/0/p/41d5b1-tag',
  },
});

export default class SpdsIndicatorTag extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-indicator-tag';
  classesToMap = ['disabled', 'filter', 'interactive', 'small'];
  elementsToRegister = [{ name: 'clearButton', selector: 'button' }];
  styles = styles;

  @equal('args.type', FILTER) filter;
  @equal('args.type', INTERACTIVE) interactive;
  @and('filter', 'args.onClose') showClose;

  <template>
    {{#if this.interactive}}
      <Button
        aria-label={{@label}}
        class={{this.classes}}
        data-test-spds-indicator-tag={{@type}}
        id={{@id}}
        type="button"
        {{on "click" @onClick}}
        {{this.validateArguments}}
        ...attributes
      >
        {{#if @icon}}
          <FlexRow @tag="span" class="spds-tag-icon" data-test-spds-indicator-tag-icon>
            {{svgJar @icon}}
          </FlexRow>
        {{/if}}
        <FlexRow @tag="span" class="spds-tag-label" data-test-spds-indicator-tag-label>
          {{@label}}
        </FlexRow>
      </Button>
    {{else}}
      <FlexRow
        @discardClick={{true}}
        @fitContent={{true}}
        @inline={{true}}
        @tag="span"
        aria-label={{@label}}
        class={{this.classes}}
        data-test-spds-indicator-tag={{@type}}
        {{this.setupComponent}}
        {{this.validateArguments}}
        ...attributes
      >
        {{#if @icon}}
          <FlexRow @tag="span" class="spds-tag-icon" data-test-spds-indicator-tag-icon>
            {{svgJar @icon}}
          </FlexRow>
        {{/if}}
        <FlexRow @tag="span" class="spds-tag-label" data-test-spds-indicator-tag-label>
          {{@label}}
        </FlexRow>
        {{#if this.showClose}}
          <Button
            aria-label="Remove {{@label}} filter"
            class="spds-filter-tag-close-button"
            data-test-spds-indicator-tag-close
            disabled={{@disabled}}
            type="button"
            {{on "click" (fn @onClose @label)}}
            {{shiftFocusToParent}}
          >
            {{svgJar "x-mark"}}
          </Button>
        {{/if}}
      </FlexRow>
    {{/if}}
  </template>
}

export const TAG_METADATA = SpdsIndicatorTag.metadata;
